import type React from 'react';

import { Text } from '@change/ds-core-ui/Text';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { ScrollContainerV2 } from '#src/app/shared/components/carousel';
import { SimpleHtml } from '#src/app/shared/components/html';

import type { AiSummaryCard, AiSummaryCardHeading } from '../../utils/buildAiSummaryCards';

// buildAiSummaryCards caps at 3 (one per CARD_DEFINITIONS entry), so this only ever needs 1-3.
// A literal lookup (rather than a `md:grid-cols-${n}` template) so Tailwind's static scanner
// picks up each class name -- it can't detect classes assembled via string interpolation.
const DESKTOP_GRID_COLS: Record<number, string> = {
	1: 'md:grid-cols-1',
	2: 'md:grid-cols-2',
	3: 'md:grid-cols-3',
};

function CardHeading({ heading }: { heading: AiSummaryCardHeading }) {
	switch (heading) {
		case 'whats_happening':
			return <Translate value="corgi.petition_details.ai_summary.cards.whats_happening_heading" />;
		case 'why_it_matters':
			return <Translate value="corgi.petition_details.ai_summary.cards.why_it_matters_heading" />;
		case 'the_ask':
			return <Translate value="corgi.petition_details.ai_summary.cards.the_ask_heading" />;
		default:
			return null;
	}
}

type Props = {
	cards: AiSummaryCard[];
	onActiveIndexChange?: (activeIndex: number, totalItems: number) => void;
};

export const AiSummaryCards: React.FC<Props> = ({ cards, onActiveIndexChange }) => {
	const { translate } = useI18n();

	return (
		<ScrollContainerV2
			as="div"
			// Names the scrollable <ul> itself (see ScrollContainerV2's own conditional tabIndex/
			// aria-label) -- distinct from the section-level aria-label in aiSummary/index.tsx,
			// which names the wrapping <section>, a different element (CHANGE-97827).
			label={translate('corgi.petition_details.ai_summary.section_description_label')}
			backgroundColor="var(--color-background-base)"
			className="w-full"
			onActiveIndexChange={onActiveIndexChange}
			// md:grid-cols-N turns the row into an explicit N-column grid at desktop, so each card
			// stretches to exactly 1/N of the row (accounting for the gaps) instead of a fixed
			// pixel width that only matches Figma at one specific container width.
			listClassName={cn('md:gap-3', DESKTOP_GRID_COLS[cards.length] ?? 'md:grid-cols-3')}
			// min-h (not a fixed h) so cards with longer generated bullets grow instead of clipping
			// their text -- the fixed heights matched Figma's placeholder copy, but real summaries
			// can run longer. md:w-auto lets the card stretch to fill its grid column instead of
			// keeping mobile's fixed w-[360px]. md:min-w-0 overrides the grid item's implicit
			// min-width:auto default -- without it, whichever card has the widest natural content
			// refuses to shrink to its 1/N column share, throwing off the other columns' widths too.
			itemClassName="border-stroke-weaker bg-background-base elevation-low min-h-[168px] w-[360px] shrink-0 rounded-md border md:min-h-48 md:w-auto md:min-w-0"
		>
			{cards.map((card) => (
				<div
					key={card.heading}
					className="flex h-full w-full flex-col gap-4 p-6"
					data-qa="ai-summary-card"
					data-testid="ai-summary-card"
				>
					<div className="flex items-center gap-2">
						<card.icon aria-hidden className="text-typography-brand h-6 w-6" />
						<Text as="span" className="text-typography-brand" fontWeight="bold" size="large" variant="body">
							<CardHeading heading={card.heading} />
						</Text>
					</div>
					<Text as="p" className="text-typography-strong" size="medium" variant="body">
						<SimpleHtml html={card.description} />
					</Text>
				</div>
			))}
		</ScrollContainerV2>
	);
};
